import { FieldValues } from 'react-hook-form';
import {
  AnyMemo,
  MemoType,
  Memo,
  CalendarMemo,
  TextMemo,
  QuoteMemo,
  TaskListMemo,
  DocMemo,
  ImageMemo,
  AudioMemo,
  VideoMemo,
  FileMemo,
  LinkMemo,
  MemoSource,
} from 'models/Memo';
import type { MemoPayload } from 'networking/mindfeed/models/Memo';
import {
  parseDateOrDefault,
  TimeZoneDateTime,
  toISOStringInUTC,
} from 'utils/DateTimeUtils';
import type { UploadItem } from '../hooks/useMediaUpload';

/** Pulls the ids of successfully-uploaded items out of a media field. */
function idsFromItems(items?: UploadItem[]): string[] {
  return (items ?? [])
    .filter((item) => item.status === 'done' && item.mediaId)
    .map((item) => item.mediaId as string);
}

/**
 * Extracts the shared fields that exist on every Memo type.
 * These come straight from the form and require no transformation.
 */
export function extractSharedMemoFields(data: FieldValues): MemoPayload {
  return {
    id: data.id as string,
    type: data.type as MemoType,
    boxId: data.boxId as string,
    collectionId: data.collectionId as string | undefined,
    createdAt: data.createdAt as string,
    datedAt: toDatedAt(data),
    lastActive: data.lastActive as number,
    isPinned: !!data.isPinned,
    pinnedAt: data.pinnedAt as string | undefined,
  };
}

/**
 * The memo's own date for the request. A picked date goes as ISO 8601. With
 * none picked, an edit sends `null` so the server falls back to the creation
 * time, and a new memo leaves it out so the server dates it to now.
 */
function toDatedAt(data: FieldValues): string | null | undefined {
  if (data.datedAt) {
    return new Date(data.datedAt as Date | string).toISOString();
  }
  return data.id ? null : undefined;
}

/**
 * Builds the optional `source` object from the form's source content.
 * We always send `type: 'auto'` and let the backend classify it as a URL or
 * plain text (mirrors the iOS `MemoSourcePayload`). Empty input is omitted.
 */
function buildSource(data: FieldValues): MemoSource | undefined {
  const content = (data.source as string | undefined)?.trim();
  return content ? { type: 'auto', content } : undefined;
}

/**
 * Returns only the type-specific fields based on data.type
 */
export function transformSpecificMemoFields(
  data: FieldValues,
): Partial<AnyMemo> {
  const type = data.type as MemoType;

  switch (type) {
    case MemoType.Calendar:
      return transformToCalendarFields(data);

    case MemoType.Text:
      return transformToTextFields(data);

    case MemoType.Link:
      return transformToLinkFields(data);

    case MemoType.Quote:
      return transformToQuoteFields(data);

    case MemoType.TaskList:
      return transformToTaskListFields(data);

    case MemoType.Doc:
      return transformToDocFields(data);

    case MemoType.Image:
      return transformToImageFields(data);

    case MemoType.Audio:
      return transformToAudioFields(data);

    case MemoType.Video:
      return transformToVideoFields(data);

    case MemoType.File:
      return transformToFileFields(data);

    default:
      return {}; // No extra fields needed
  }
}

export function transformToImageFields(
  data: FieldValues,
): Omit<ImageMemo, keyof Memo> {
  return {
    imageIds: idsFromItems(data.imageIds as UploadItem[] | undefined),
    title: data.title?.trim() || undefined,
    // The optional caption is edited via the shared `content` field.
    description: data.content?.trim() || undefined,
  };
}

export function transformToAudioFields(
  data: FieldValues,
): Omit<AudioMemo, keyof Memo> {
  return {
    audioIds: idsFromItems(data.audioIds as UploadItem[] | undefined),
    title: data.title?.trim() || undefined,
    description: data.content?.trim() || undefined,
  };
}

export function transformToVideoFields(
  data: FieldValues,
): Omit<VideoMemo, keyof Memo> {
  return {
    videoIds: idsFromItems(data.videoIds as UploadItem[] | undefined),
    title: data.title?.trim() || undefined,
    // The optional caption is edited via the shared `content` field.
    description: data.content?.trim() || undefined,
  };
}

export function transformToFileFields(
  data: FieldValues,
): Omit<FileMemo, keyof Memo> {
  return {
    fileIds: idsFromItems(data.fileIds as UploadItem[] | undefined),
    title: data.title?.trim() || undefined,
    // The optional caption is edited via the shared `content` field.
    description: data.content?.trim() || undefined,
  };
}

export function transformToCalendarFields(
  data: FieldValues,
): Omit<CalendarMemo, keyof Memo> {
  const isAllDay = !!data.isAllDay;
  const isMoment = !!data.isMoment;

  const baseStart = parseDateOrDefault(data.startAt);
  const baseEnd = parseDateOrDefault(data.endAt, baseStart);

  const startDate = new Date(baseStart);
  const endDate = new Date(baseEnd);

  if (isAllDay) {
    startDate.setUTCHours(0, 0, 0, 0);
    if (isMoment) {
      endDate.setTime(startDate.getTime());
    } else {
      endDate.setUTCHours(23, 59, 59, 999);
    }
  }

  const start: TimeZoneDateTime = {
    dateTime: toISOStringInUTC(startDate),
    timezone: 'UTC',
  };

  const end: TimeZoneDateTime = {
    dateTime: toISOStringInUTC(endDate),
    timezone: 'UTC',
  };

  return {
    name: (data.name ?? '').trim(),
    description: data.description?.trim() || null,
    start,
    end,
    isAllDay,
    isMoment,
  };
}

export function transformToTextFields(
  data: FieldValues,
): Omit<TextMemo, keyof Memo> {
  return {
    content: (data.content ?? '').trim(),
    title: data.title?.trim() || null,
    source: buildSource(data),
  };
}

export function transformToLinkFields(
  data: FieldValues,
): Omit<LinkMemo, keyof Memo> {
  return {
    content: (data.content ?? '').trim(),
    title: data.title?.trim() || null,
    description: data.description?.trim() || undefined,
  };
}

export function transformToQuoteFields(
  data: FieldValues,
): Omit<QuoteMemo, keyof Memo> {
  return {
    content: (data.content ?? '').trim(),
    author: (data.author ?? '').trim(),
    title: data.title?.trim() || null,
    description: data.description?.trim() || undefined,
    source: buildSource(data),
  };
}

export function transformToTaskListFields(
  data: FieldValues,
): Omit<TaskListMemo, keyof Memo> {
  return {
    title: data.title?.trim() || null,
    tasks: data.tasks
      ? JSON.stringify({ version: '1', items: data.tasks })
      : undefined,
    description: data.description?.trim() || undefined,
  };
}

export function transformToDocFields(
  data: FieldValues,
): Omit<DocMemo, keyof Memo> {
  return {
    title: data.title?.trim() || null,
    document: data.document ? JSON.stringify(data.document) : undefined,
  };
}
